import React, { FC, PropsWithChildren } from 'react'

import { styled } from '@linaria/react'
import loadable from '@loadable/component'
import { FormattedMessage } from 'react-intl'

import { hideScrollBarStyles } from 'common/styles/hideScrollBar'
import { PrimaryButton } from 'components/designSystem/Button/PrimaryButton'
import { breakpoints } from 'components/designSystem/shared/breakpoints'
import { Heart4Svg } from 'components/designSystem/svgr/Heart4Svg'
import { Typography } from 'components/designSystem/Typography/Typography'
import { useShowLinkBlog } from 'hooks/useShowLinkBlog'

import { AnimatedBlock } from './AnimatedBlock'
import background1 from './assets/background/1.svg'
import background2 from './assets/background/2.svg'
import background3 from './assets/background/3.svg'
import facesCards from './assets/faces_cards.png'
import facesCardsWebP from './assets/faces_cards.webp'
import facesInCircles from './assets/faces_in_circles.png'
import facesInCirclesWebP from './assets/faces_in_circles.webp'
import settingsDemoPoster from './assets/settings_demo_poster.png'
import { DateBook } from './DateBook/DateBook'
import { DownloadApp } from './DownloadApp/DownloadApp'
import { Footer } from './Footer/Footer'
import { Navigation } from './Navigation'
import { Picture } from './Picture'
import { RegisterButton } from './RegisterButton'
import { RobotView } from './RobotView'
import { ShowAfterIntersection } from './ShowAfterIntersection'
import { useScrollCssVar } from './useScrollCssVar'
/** Оставляю пока постеры, может где-то видео не воспроизведется */
// import likesPoster from './video/likes.png'
// import videoCallPoster from './video/video_call.png'
import { loginVariantsPath } from '../Boarding/boarding.paths'

const Main = styled.main`
  display: flex;
  flex-direction: column;
  background-color: var(--static-white);
  ${hideScrollBarStyles};
`

const Union = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-size: auto 75%;
  background-repeat: repeat-x;
  background-position-x: -40px;
  background-position-y: center;
`

const Text = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  /* Увеличил до 34-х, чтобы текст в кнопках не переносился */
  max-width: 34vw;

  @media (max-width: ${breakpoints.mobile - 1}px) {
    max-width: 100%;
    text-align: center;
  }
`

const GAP = '15px'

const Header: FC<
  {
    /**
     * H1 должен быть только один на странице, важно для SEO:
     * https://youtrack.mamba.ru/issue/M-12184
     */
    main?: boolean
  } & PropsWithChildren
> = ({ main, children }) => (
  <Typography tag={main ? 'h1' : 'h2'} fontSize={40} fontWeight={700}>
    {children}
  </Typography>
)

const Subtitle: FC<PropsWithChildren> = ({ children }) => (
  <Typography fontSize={20} fontWeight={400} style={{ marginTop: GAP }}>
    {children}
  </Typography>
)

const ButtonPanel = styled.div`
  margin-top: ${GAP};
  display: flex;
  flex-direction: row;
  gap: 14px;

  @media (max-width: ${breakpoints.mobile - 1}px) {
    flex-direction: column;
    align-self: center;
    align-items: stretch;
  }
`

const VideoPoster = styled.img`
  max-width: 100%;
  height: auto;
`

export const WaveBoardingIndex: FC = () => {
  useScrollCssVar()
  const hasLinkBlog = useShowLinkBlog()

  return (
    <Main>
      {/* Общий фон HTML, чтобы не было белого засвета при блокированном инерциальном скролее */}
      <style
        dangerouslySetInnerHTML={{
          __html: 'html { background-color: var(--pearl-soft); }',
        }}
      />
      <Navigation />
      <Union
        style={{
          backgroundImage: `url(${background1})`,
          backgroundPositionX: 'calc(var(--scroll, 0) * 100% + 30%)',
          backgroundPositionY: 0,
        }}
      >
        <AnimatedBlock>
          <Text>
            <Header main={true}>
              <FormattedMessage
                id="wave_boarding.title"
                defaultMessage="Твои новые знакомства"
              />
            </Header>
            <Subtitle>
              <FormattedMessage
                id="wave_boarding.subtitle"
                defaultMessage="Миллионы активных пользователей с такими же целями и интересами уже ждут тебя — сделай шаг навстречу."
              />
            </Subtitle>
            <ButtonPanel>
              <RegisterButton
                icon={
                  <Heart4Svg
                    size={22}
                    color="var(--accent-primary-on-accent-primary)"
                  />
                }
                data-name="create-profile-action"
              >
                <FormattedMessage
                  id="app.create.profile"
                  defaultMessage="Создать профиль"
                />
              </RegisterButton>

              <PrimaryButton
                size="M"
                to={loginVariantsPath}
                color="white"
                data-name="login-action"
              >
                <FormattedMessage id="app.enter" defaultMessage="Войти" />
              </PrimaryButton>
            </ButtonPanel>
          </Text>
          <Picture png={{ src: facesCards }} webp={{ src: facesCardsWebP }} />
        </AnimatedBlock>
      </Union>

      <Union
        style={{
          backgroundImage: `url(${background2})`,
          backgroundPositionX: 'calc(var(--scroll, 0) * 100% + 45%)',
        }}
      >
        <AnimatedBlock type="reversed">
          {/* Поменять png, т.к. по размеру 300 Кб почти, зачем такое "превью" */}
          {/* <VideoPoster src={likesPoster} /> */}
          <ShowAfterIntersection>
            <LikesVideoLoadable />
          </ShowAfterIntersection>
          <Text>
            <Header>
              <FormattedMessage
                id="wave_boarding.title_2"
                defaultMessage="Будь в центре внимания"
              />
            </Header>
            <Subtitle>
              <FormattedMessage
                id="wave_boarding.subtitle_2"
                defaultMessage="Получай много внимания и лайков от живой и активной аудитории Мамбы — твоя самооценка скажет спасибо."
              />
            </Subtitle>
            <ButtonPanel>
              <RegisterButton data-name="get-attention-action">
                <FormattedMessage
                  id="app.get_attention"
                  defaultMessage="Получить внимание"
                />
              </RegisterButton>
            </ButtonPanel>
          </Text>
        </AnimatedBlock>

        <AnimatedBlock>
          <Text>
            <Header>
              <FormattedMessage
                id="wave_boarding.title_3"
                defaultMessage="Не дожидайся взаимного мэтча — сразу пиши тем, кто нравится"
              />
            </Header>
            <Subtitle>
              <FormattedMessage
                id="wave_boarding.subtitle_3"
                defaultMessage="Поиск по параметрам поможет подобрать подходящих людей для знакомства и сразу написать им."
              />
            </Subtitle>
            <ButtonPanel>
              <RegisterButton data-name="find-action">
                <FormattedMessage id="app.find" defaultMessage="Найти" />
              </RegisterButton>
            </ButtonPanel>
          </Text>
          <Picture
            png={{ src: facesInCircles }}
            webp={{ src: facesInCirclesWebP }}
          />
        </AnimatedBlock>
      </Union>

      <Union
        style={{
          backgroundImage: `url(${background3})`,
          backgroundPositionX: 'calc(var(--scroll, 0) * 100%)',
          backgroundPositionY: 'bottom',
        }}
      >
        <AnimatedBlock type="reversed">
          <ShowAfterIntersection
            thumbnail={<VideoPoster src={settingsDemoPoster} loading="lazy" />}
          >
            <SettingsDemoLoadable />
          </ShowAfterIntersection>
          <Text>
            <Header>
              <FormattedMessage
                id="wave_boarding.title_4"
                defaultMessage="Настрой свои правила знакомства"
              />
            </Header>
            <Subtitle>
              <FormattedMessage
                id="wave_boarding.subtitle_4"
                defaultMessage="Если ценишь конфиденциальность, воспользуйся настройками приватности и не переживай за своё личное пространство."
              />
            </Subtitle>
            <ButtonPanel>
              <RegisterButton data-name="try-action">
                <FormattedMessage
                  id="form.button.try"
                  defaultMessage="Попробовать"
                />
              </RegisterButton>
            </ButtonPanel>
          </Text>
        </AnimatedBlock>

        <AnimatedBlock>
          <Text>
            <Header>
              <FormattedMessage
                id="wave_boarding.title_5"
                defaultMessage="Знакомься с реальными людьми"
              />
            </Header>
            <Subtitle>
              <FormattedMessage
                id="wave_boarding.subtitle_5"
                defaultMessage="Проверка профилей, антиспам-система, видеозвонки помогут сделать знакомства и встречи безопасными."
              />
            </Subtitle>
            <ButtonPanel>
              <RegisterButton data-name="meet-action">
                <FormattedMessage
                  id="app.meet"
                  defaultMessage="Познакомиться"
                />
              </RegisterButton>
            </ButtonPanel>
          </Text>
          <ShowAfterIntersection>
            <VideoCallLoadable />
          </ShowAfterIntersection>
        </AnimatedBlock>

        <RobotView />
      </Union>

      <DownloadApp />

      {hasLinkBlog && <DateBook />}

      <Footer />
    </Main>
  )
}

const LikesVideoLoadable = loadable(() => import('./video/LikesVideo'))
const SettingsDemoLoadable = loadable(
  () => import('./SettingDemo/SettingsDemo')
)
const VideoCallLoadable = loadable(() => import('./video/VideoCallVideo'))

export default WaveBoardingIndex
